// Cost formatting + ad-hoc LLM op estimation. The per-block estimate lives
// on the backend (cost.py); this is for client-side ops like 字幕校对 (polish)
// where the user needs to see cost BEFORE submitting and the backend hasn't
// touched the text yet.

// 2026-06-12 pricing redesign: users never see credits. The backend
// still meters in integer credits; this single choke point converts
// them to the user-facing unit — rendered MINUTES (1 min = 10 credits,
// mirrors backend pricing.py). Every ✦ display flowed through here, so
// the whole UI flips units in one move.
const CREDITS_PER_MINUTE = 10;

export function formatCost(cost: number): string {
  if (cost <= 0) return 'free';
  if (cost < CREDITS_PER_MINUTE) return '<1 min';
  const min = cost / CREDITS_PER_MINUTE;
  return `≈${min % 1 === 0 ? min.toFixed(0) : min.toFixed(1)} min`;
}

export function creditsToMinutes(credits: number): number {
  return Math.round((credits / CREDITS_PER_MINUTE) * 10) / 10;
}


// Mirror cost.py heuristic for LLM polish:
//   tokens ≈ chars × 1.5  (Chinese-heavy worst case)
//   cost  = (in_tokens/1k) × in_rate + (out_tokens/1k) × out_rate
//   final = base × markup
// Polish output is roughly the same length as input, so we use chars on
// both sides. Returns 0 for empty input.
export function estimatePolishCost(
  chars: number,
  inRatePer1k: number,
  outRatePer1k: number,
  markup: number,
): number {
  if (chars <= 0) return 0;
  const tokens = chars * 1.5;
  const base = (tokens / 1000) * inRatePer1k + (tokens / 1000) * outRatePer1k;
  return base * markup;
}

// Estimate transcribe cost from audio duration. Mirrors backend
// cost.py:RATES["whisper_per_sec"] (default ¥0.0005/sec). For 增强转写
// add the polish cost on top — char count for polish is roughly the
// transcript length, which we estimate from duration (≈ 3 chars/sec
// average Chinese speech).
const WHISPER_PER_SEC_DEFAULT = 0.0005;
const CHARS_PER_SEC_SPEECH = 3;
export function estimateTranscribeCost(
  durationSec: number,
  markup: number,
  enhanced: boolean,
  inRatePer1k = 0.005,
  outRatePer1k = 0.015,
): number {
  if (durationSec <= 0) return 0;
  const whisper = durationSec * WHISPER_PER_SEC_DEFAULT;
  if (!enhanced) return whisper * markup;
  const chars = durationSec * CHARS_PER_SEC_SPEECH;
  const polish = estimatePolishCost(chars, inRatePer1k, outRatePer1k, 1);
  return (whisper + polish) * markup;
}
